import axios from 'axios';

// Use production URL for deployment, localhost for development
const API_BASE_URL = process.env.REACT_APP_API_URL || 
    (process.env.NODE_ENV === 'production' 
        ? 'https://interview.b2world.info/api' 
        : 'http://localhost:3001/api');

class AuthService {
    constructor() {
        this.token = localStorage.getItem('adminToken');
        this.setupAxiosInterceptors();
    }

    setupAxiosInterceptors() {
        // Request interceptor to add auth token
        axios.interceptors.request.use(
            (config) => {
                const token = localStorage.getItem('adminToken');
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            (error) => {
                return Promise.reject(error);
            }
        );

        // Response interceptor to handle auth errors
        axios.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error.response?.status === 401) {
                    // Token expired or invalid
                    this.logout();
                    window.location.href = '/login';
                }
                return Promise.reject(error);
            }
        );
    }

    async login(username, password) {
        try {
            const response = await axios.post(`${API_BASE_URL}/auth/login`, {
                username: username.trim(),
                password
            });

            if (response.data.success) {
                const { token, admin } = response.data;
                
                // Store token and admin data
                localStorage.setItem('adminToken', token);
                localStorage.setItem('adminData', JSON.stringify(admin));
                
                this.token = token;
                
                return {
                    success: true,
                    admin,
                    token
                };
            } else {
                return {
                    success: false,
                    message: response.data.message || 'Login failed'
                };
            }
        } catch (error) {
            console.error('Login error:', error);
            
            let errorMessage = 'Login failed. Please try again.';
            
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.response?.status === 401) {
                errorMessage = 'Invalid username or password';
            } else if (error.response?.status === 423) {
                errorMessage = error.response.data.message || 'Account is locked';
            }
            
            return {
                success: false,
                message: errorMessage,
                locked: error.response?.data?.locked || false,
                remainingAttempts: error.response?.data?.remainingAttempts
            };
        }
    }

    logout() {
        // Remove token and admin data from localStorage
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminData');
        this.token = null;
    }

    async getProfile() {
        try {
            const response = await axios.get(`${API_BASE_URL}/auth/profile`);
            return response.data;
        } catch (error) {
            console.error('Get profile error:', error);
            throw error;
        }
    }

    async changePassword(currentPassword, newPassword) {
        try {
            const response = await axios.put(`${API_BASE_URL}/auth/change-password`, {
                currentPassword,
                newPassword
            });
            return response.data;
        } catch (error) {
            console.error('Change password error:', error);
            throw error;
        }
    }

    isAuthenticated() {
        const token = localStorage.getItem('adminToken');
        const adminData = localStorage.getItem('adminData');
        return !!(token && adminData);
    }

    getAdminData() {
        try {
            const adminData = localStorage.getItem('adminData');
            return adminData ? JSON.parse(adminData) : null;
        } catch (error) {
            console.error('Error parsing admin data:', error);
            return null;
        }
    }

    getToken() {
        return localStorage.getItem('adminToken');
    }
}

const authService = new AuthService();
export default authService;
